import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Terms and privacy for the Auction Calendar.
 *
 * Adapted from the Your First Parcel site's own pages so that a subscriber
 * reads the same voice and the same company details in both places, and then
 * narrowed to this product. The guide is a one off purchase with a 7 day
 * refund window; the calendar is a subscription, and the terms that matter to a
 * subscriber are different: what they are billed, when it renews, what
 * cancelling does, and what the dates actually promise.
 *
 * Written as components rather than as static HTML because this app is served
 * by the same React build on the calendar hostname, and because the colours
 * have to be theme tokens to survive dark mode like every other calendar page.
 */

const EFFECTIVE = 'Effective September 18, 2026';
const CONTACT = 'cwaffiliateinvestments@gmail.com';

function Page({ title, children }) {
  return (
    <div style={{ maxWidth: '680px', margin: '0 auto', padding: '32px 16px 64px' }}>
      <h1 style={{ color: 'var(--color-heading)', fontSize: '1.6rem', marginBottom: '6px' }}>{title}</h1>
      <p style={{
        color: 'var(--color-text-muted)',
        fontSize: '0.8rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        fontWeight: 600,
        marginTop: 0,
        marginBottom: '28px'
      }}>
        {EFFECTIVE}
      </p>
      {children}
      <p style={{
        marginTop: '40px',
        paddingTop: '20px',
        borderTop: '1px solid var(--color-border)',
        color: 'var(--color-text-muted)',
        fontSize: '0.85rem'
      }}>
        This page is provided for transparency and is not a substitute for legal advice.
      </p>
      <p style={{ marginTop: '16px', fontSize: '0.9rem' }}>
        <Link to="/terms" style={{ color: 'var(--color-primary)' }}>Terms</Link>
        <span style={{ color: 'var(--color-text-muted)', padding: '0 8px' }}>·</span>
        <Link to="/privacy" style={{ color: 'var(--color-primary)' }}>Privacy</Link>
        <span style={{ color: 'var(--color-text-muted)', padding: '0 8px' }}>·</span>
        <Link to="/login" style={{ color: 'var(--color-primary)' }}>Sign in</Link>
      </p>
    </div>
  );
}

function H2({ children }) {
  return (
    <h2 style={{
      fontSize: '1.1rem',
      color: 'var(--color-primary)',
      margin: '32px 0 10px',
      fontWeight: 700
    }}>
      {children}
    </h2>
  );
}

const pStyle = { lineHeight: 1.75, marginBottom: '14px', color: 'var(--color-text)' };

export function CalendarTerms() {
  return (
    <Page title="Terms of Service">
      <p style={pStyle}>
        These are the terms you agree to by subscribing to the Auction Calendar.
        They are written plainly on purpose.
      </p>

      <H2>What the subscription costs, and when it renews</H2>
      <p style={pStyle}>
        The Auction Calendar is <strong>$24 per month</strong> or <strong>$199 per year</strong>.
        It renews automatically at the end of each period using the card you
        saved, until you cancel it.
      </p>
      <p style={pStyle}>
        You can move from the monthly plan to the annual plan at any time. Square
        handles the billing for that change and applies credit for the part of
        the month you have already paid for.
      </p>

      <H2>Cancelling</H2>
      <p style={pStyle}>
        You can cancel at any time from your account page. Cancelling takes
        effect <strong>at the end of the period you have already paid for</strong>,
        and you keep full access until then. Nothing is taken away on the day you
        click cancel.
      </p>
      <p style={pStyle}>
        <strong>Partial periods are not refunded.</strong> If you cancel halfway
        through a month or a year, you keep the rest of it and are not billed
        again. Changing your mind before the period ends is one button on the
        same page.
      </p>
      <p style={pStyle}>
        If a payment fails, your calendar stays available for a short grace
        period while you replace the card. If it is not replaced by then, the
        subscription is cancelled. You can resubscribe afterwards and your
        account, your states and your email settings are all still there.
      </p>

      <H2>What the dates are, and what they are not</H2>
      <p style={pStyle}>
        <strong>Dates are provided as researched, and must be confirmed with the county.</strong>
        {' '}Every entry is compiled by hand from county and state sources and is
        kept current to the best of our ability. Counties move sale dates, cancel
        sales, change platforms, and post lists late or not at all, often without
        announcing it.
      </p>
      <p style={pStyle}>
        <strong>Always confirm directly with the county before you travel, register, or bid.</strong>
        {' '}This calendar is a research tool that tells you where to look and
        when to start looking. It is not an official notice of sale, and it is
        not a substitute for the county's own published notice.
      </p>
      <p style={pStyle}>
        We do not guarantee that any sale listed will happen, that any date shown
        is final, or that any list linked will remain available. A sale that is
        missing, wrong, or cancelled is not a failure of the subscription.
      </p>

      <H2>This is information, not advice</H2>
      <p style={pStyle}>
        Everything in the calendar is <strong>informational</strong>. It is not
        legal advice, it is not tax advice, and it is not investment advice.
        Buying land at a tax sale carries real risk,
        <strong> including the total loss of what you put in</strong>. You are
        responsible for your own due diligence and for every decision you make
        with your own money.
      </p>

      <H2>Your account</H2>
      <p style={pStyle}>
        Your subscription is for you. Please do not share your login, and do not
        republish or resell the calendar data.
      </p>

      <H2>Who you are dealing with</H2>
      <p style={pStyle}>
        The Auction Calendar is published by <strong>CW Affiliate Investments, LLC</strong>,
        operating as Your First Parcel.
      </p>
      <p style={{ ...pStyle, color: 'var(--color-primary)', fontWeight: 600, wordBreak: 'break-word' }}>
        {CONTACT}
      </p>
    </Page>
  );
}

export function CalendarPrivacy() {
  return (
    <Page title="Privacy Policy">
      <p style={pStyle}>
        This page explains what we collect when you subscribe to the Auction
        Calendar, why we collect it, who else touches it, and how to get it
        removed. It is written to be read, not to be endured.
      </p>

      <H2>Who we are</H2>
      <p style={pStyle}>
        The Auction Calendar is operated by <strong>CW Affiliate Investments, LLC</strong>.
        When this page says "we" or "us", that is who it means.
      </p>

      <H2>What we collect</H2>
      <p style={pStyle}>
        When you subscribe we receive your <strong>email address</strong>, the
        password you choose, and the states you ask the weekly email to cover.
        Your password is stored only as a one way hash, so nobody here can read
        it, including us.
      </p>
      <p style={pStyle}>
        <strong>We never see or store your card number.</strong> The payment form
        is Square's own, and it hands us back a token that stands in for the
        card. What we keep is that token, the card brand and its last four
        digits, so the account page can tell you which card is on file.
      </p>
      <p style={pStyle}>
        Our hosting provider also keeps standard server logs, recording things
        like IP address and browser type. They are generated automatically for
        security and troubleshooting, and we do not use them to build a profile
        of you.
      </p>

      <H2>Why we collect it</H2>
      <p style={pStyle}>
        We use your email address to sign you in, to send the weekly digest if
        you want it, and to tell you about your own subscription, such as a
        payment that did not go through. We use your chosen states to decide what
        goes in that digest.
      </p>
      <p style={pStyle}>
        <strong>Every digest includes a way to unsubscribe.</strong> Unsubscribing
        turns off the email and nothing else: it never cancels the subscription
        you are paying for.
      </p>

      <H2>Who else processes it</H2>
      <p style={pStyle}>These service providers handle your information on our behalf:</p>
      <ul style={{ margin: '0 0 14px 22px' }}>
        <li style={{ lineHeight: 1.75, marginBottom: '8px' }}>
          <strong>Square</strong> processes payments and stores your card.
        </li>
        <li style={{ lineHeight: 1.75, marginBottom: '8px' }}>
          <strong>SendGrid</strong> delivers our email.
        </li>
        <li style={{ lineHeight: 1.75, marginBottom: '8px' }}>
          <strong>Netlify</strong> and <strong>Railway</strong> host the site and its database.
        </li>
      </ul>
      <p style={pStyle}>
        Each receives what it needs to do that job and nothing beyond it.
        <strong> We do not sell or rent personal information</strong>, and we do
        not hand it to anyone else to use for their own marketing.
      </p>

      <H2>How long we keep it</H2>
      <p style={pStyle}>
        We keep your information while your subscription is active, and after a
        cancellation so that resubscribing does not mean starting again. If you
        ask us to remove it, we remove it.
      </p>

      <H2>Your choices</H2>
      <p style={pStyle}>
        You can turn the weekly email off from your account page or from the link
        in any digest. You can change your password or cancel from the same page.
        You can also ask us what we hold about you, ask us to correct it, or ask
        us to delete it.
      </p>

      <H2>Contact for privacy requests</H2>
      <p style={pStyle}>
        Send privacy requests and questions to this address, written here as
        plain text:
      </p>
      <p style={{ ...pStyle, color: 'var(--color-primary)', fontWeight: 600, wordBreak: 'break-word' }}>
        {CONTACT}
      </p>
      <p style={pStyle}>
        It helps if you write from the same email address you subscribed with, so
        we can find your record without asking you for more information than we
        already have.
      </p>
      <p style={pStyle}>CW Affiliate Investments, LLC</p>
    </Page>
  );
}
